import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Grid, Box, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { CardAttribute, ModalLayout } from '@/components'
import { EntityPhoto } from '@/components/ui'
import {
  useMutationWrapper,
  usePostExpenseDeclarationApprove,
  usePostExpenseDeclarationDecline,
} from '@/hooks'
import {
  ExpenseDeclaration as ExpenseDeclarationType,
  ExpenseStatuses,
} from '@/types'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { ControlledTextField } from '@/components/inputs'
import dayjs from 'dayjs'
import { DATE_FORMAT_DMY } from '@/constants'
import { ExpenseStatusIndicator } from '@/features/expensesDeclaration/ExpenseStatusIndicator'

export type ExpenseDeclarationDetailProps = {
  expenseDeclarationData: ExpenseDeclarationType
}

export type ExpenseDeclarationStatusFormProps = {
  comment: string
}

export const ExpenseDeclarationDetail = ({
  expenseDeclarationData,
}: ExpenseDeclarationDetailProps): JSX.Element => {
  const { t } = useTranslation('expensesDeclaration')
  const [data, setData] = useState<ExpenseDeclarationType>(
    expenseDeclarationData
  )

  const { mutateAsync: approve, isLoading: approving } = useMutationWrapper(
    () => usePostExpenseDeclarationApprove({ id: expenseDeclarationData.id })
  )

  const { mutateAsync: decline, isLoading: declining } = useMutationWrapper(
    () => usePostExpenseDeclarationDecline({ id: expenseDeclarationData.id })
  )

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm<ExpenseDeclarationStatusFormProps>({
    mode: 'onChange',
    defaultValues: {
      comment: '',
    },
    resolver: yupResolver(
      yup.object({
        comment: yup.string().required(),
      })
    ),
  })

  const handleApprove = async (formData: ExpenseDeclarationStatusFormProps) => {
    const params = {
      validation_message: formData.comment,
    }

    await approve(params, {
      successMessageKey: 'expensesDeclaration:notifications.approved',
      onSuccess: (data) => {
        setData(data)
      },
    })
  }

  const handleDecline = async (formData: ExpenseDeclarationStatusFormProps) => {
    const params = {
      validation_message: formData.comment,
    }

    await decline(params, {
      successMessageKey: 'expensesDeclaration:notifications.declined',
      onSuccess: (data) => {
        setData(data)
      },
    })
  }

  const onSubmitForm = (e: FormEvent) => {
    const action = (
      e.nativeEvent as SubmitEvent
    ).submitter?.attributes.getNamedItem('name')?.value
    const handler = action === 'approve' ? handleApprove : handleDecline

    handleSubmit(async (data) => handler(data))(e)
  }

  const canApprove =
    data.status === ExpenseStatuses.PENDING ||
    data.status === ExpenseStatuses.DECLINED
  const canDecline =
    data.status === ExpenseStatuses.PENDING ||
    data.status === ExpenseStatuses.APPROVED

  return (
    <ModalLayout title={t('detailTitle', { id: data.id })}>
      <Grid container={true} rowSpacing={5} width={{ md: 925, sm: 'auto' }}>
        <>
          {!data ? (
            <Typography>{t('expense-declaration.noData')}</Typography>
          ) : (
            <>
              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('expense-declaration.fieldTitles.date')}
                    value={dayjs(data.date).format(DATE_FORMAT_DMY)}
                  />
                </Grid>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('expense-declaration.fieldTitles.amount')}
                    value={`€ ${data.amount}`}
                  />
                </Grid>
              </Grid>

              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t(
                      'expense-declaration.fieldTitles.reimbursementType'
                    )}
                    value={t(`reimbursementTypes.${data.reimbursement_type}`)}
                  />
                </Grid>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('expense-declaration.fieldTitles.description')}
                    value={data.description}
                  />
                </Grid>
              </Grid>

              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('expense-declaration.fieldTitles.researcher')}
                    value={`${data.researcher.first_name} ${data.researcher.last_name}`}
                  />
                </Grid>
                {data.approved_by && (
                  <Grid xs={6} item={true}>
                    <CardAttribute
                      title={t('expense-declaration.fieldTitles.approvedBy')}
                      value={`${data.approved_by.first_name} ${data.approved_by.last_name}`}
                    />
                  </Grid>
                )}
              </Grid>

              <Grid xs={12} item={true} container={true} spacing={4}>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('expense-declaration.fieldTitles.createdAt')}
                    value={dayjs(data.created_at).format(DATE_FORMAT_DMY)}
                  />
                </Grid>
                <Grid xs={6} item={true}>
                  <CardAttribute
                    title={t('expense-declaration.fieldTitles.updatedAt')}
                    value={dayjs(data.updated_at).format(DATE_FORMAT_DMY)}
                  />
                </Grid>
              </Grid>

              <Grid xs={6} item={true}>
                <CardAttribute
                  title={t('expense-declaration.fieldTitles.invoiceNumber')}
                  value={data.invoice_number || '-'}
                />
              </Grid>

              {data.receipts && data.receipts.length > 0 && (
                <Grid xs={12} item={true}>
                  <Typography variant="caption" color="grey.600" mb={2}>
                    {t('expense-declaration.fieldTitles.receipts')}
                  </Typography>
                  <Grid container={true} spacing={4} mt={2}>
                    {data.receipts.map((receipt) => (
                      <EntityPhoto
                        key={receipt.id}
                        photo={{
                          id: receipt.id,
                          link: receipt.link,
                          type: 'image',
                        }}
                      />
                    ))}
                  </Grid>
                </Grid>
              )}

              <Grid xs={12} item={true} container={true}>
                <Grid xs={12} item={true}>
                  <Stack direction="row" gap={4} alignItems="center" mt={4}>
                    <CardAttribute
                      title={t('expense-declaration.fieldTitles.status')}
                      value={
                        <ExpenseStatusIndicator
                          isSmall={false}
                          status={data.status}
                          name={t(`indicator.${data.status}`)}
                        />
                      }
                    />
                  </Stack>
                </Grid>
                {!!data.validation_message && (
                  <Grid xs={12} item={true}>
                    <CardAttribute
                      title={t('expense-declaration.fieldTitles.comment')}
                      value={data.validation_message}
                      mt={4}
                    />
                  </Grid>
                )}
                {(canApprove || canDecline) && (
                  <Box mt={6}>
                    <form onSubmit={onSubmitForm}>
                      <Stack spacing={2} alignItems="end">
                        <Box width={600}>
                          <ControlledTextField
                            name="comment"
                            control={control}
                            placeholder={t('placeholders.comment')}
                            multiline={true}
                            rows={2}
                            inputProps={{
                              style: {
                                padding: 0,
                              },
                            }}
                          />
                        </Box>
                        <Stack direction="row" gap={4}>
                          {canDecline && (
                            <LoadingButton
                              variant="contained"
                              color="error"
                              type="submit"
                              name="decline"
                              loading={declining}
                              disableElevation={true}
                              disabled={!isDirty || approving || declining}
                            >
                              <span>
                                {t('expense-declaration.buttons.decline')}
                              </span>
                            </LoadingButton>
                          )}
                          {canApprove && (
                            <LoadingButton
                              variant="contained"
                              color="secondary"
                              type="submit"
                              name="approve"
                              loading={approving}
                              disableElevation={true}
                              disabled={!isDirty || approving || declining}
                            >
                              <span>
                                {t('expense-declaration.buttons.approve')}
                              </span>
                            </LoadingButton>
                          )}
                        </Stack>
                      </Stack>
                    </form>
                  </Box>
                )}
              </Grid>
            </>
          )}
        </>
      </Grid>
    </ModalLayout>
  )
}
